import axios from 'axios';
import { getConfig } from '../../config';
import { TimeBucket } from '../constants';
import _ from 'lodash';

const tsiId = getConfig().telemetryApi.tsiId;

export const toTSITimespan = (timespan) => {
  return {
    from: timespan.from.toUTC().toISO(),
    to: timespan.to.toUTC().toISO()
  };
};

const aggregateDelimiter = '--agg--';
const toAggregateName = (signalName, aggregate) => `${signalName}${aggregateDelimiter}${aggregate}`;
const fromAggregateName = (aggregateName) => aggregateName.split(aggregateDelimiter);

const removeMetaProps = (tsiData) => {
  const metaProps = [
    'entityId',
    'entityType',
    'receivedTimestamp'
  ];

  let i = tsiData.properties.length;
  while (i--) {
    if (metaProps.includes(tsiData.properties[i].name)) {
      tsiData.properties.splice(i, 1);
    }
  }
};

export const convertTsiDataToGraphData = (tsiData, assetSignals) => {
  removeMetaProps(tsiData);

  const graphData = [];

  for (let prop of tsiData.properties) {
    const propNameAndAggregate = fromAggregateName(prop.name);

    const propGroup = {
      assetId: assetSignals.assetId,
      name: propNameAndAggregate[0],
      data: []
    };

    if (propNameAndAggregate.length === 2) {
      propGroup.aggregate = propNameAndAggregate[1];
    }

    for (let i = 0; i < prop.values.length; ++i) {
      if (prop.values[i] != null) {
        propGroup.data.push([
          new Date(tsiData.timestamps[i]).getTime(),
          prop.values[i]
        ]);
      }
    }

    graphData.push(propGroup);
  }

  // Add empty objects for signals that are missing from the retrieved data
  for (let assetSignal of assetSignals.signals) {
    if (!graphData.some(signal => signal.name === assetSignal.name)) {
      graphData.push({
        assetId: assetSignals.assetId,
        name: assetSignal.name,
        data: [],
        laneNumber: assetSignal.laneNumber || 0,
        color: assetSignal.color,
        decimalPoint: assetSignal.decimalPoint || 2,
        type: assetSignal.type,
        fullName: assetSignal.fullName
      });
    }
  }

  const transformedGraphData = graphData.map(data => {
    const index = assetSignals.signals.findIndex(signal => signal.name === data.name);

    const hasFound = index >= 0;

    return {
      ...data,
      laneNumber: hasFound ? assetSignals.signals[index].laneNumber : 0,
      color: assetSignals.signals[index].color,
      decimalPoint: assetSignals.signals[index].decimalPoint || 2,
      type: assetSignals.signals[index].type,
      fullName: assetSignals.signals[index].fullName
    };
  });

  return transformedGraphData;
};

export const baseTsiUrl = (_tsiId) => {
  return `https://${_tsiId}.env.timeseries.azure.com`;
};

const createTsiUrl = (_tsiId) => {
  return baseTsiUrl(_tsiId) + '/timeseries/query?api-version=2020-07-31';
};

const mapToTsiSignalType = (signalType) => {
  switch (signalType.toLowerCase()) {
    case 'float': return 'Double';
    case 'integer': return 'Long';
    case 'boolean': return 'Bool';
    case 'duration': return 'TimeSpan';
    case 'time':
    case 'date': return 'DateTime';
    default: return _.capitalize(signalType);
  }
};

const createEventProjectedProps = (assetSignals) => {
  return assetSignals.signals.map(signal => ({
    name: signal.name,
    type: mapToTsiSignalType(signal.type)
  }));
};


export const createEventRequestData = (assetSignals, timespan) => {
  return {
    getEvents: {
      timeSeriesId: [assetSignals.assetId],
      searchSpan: timespan,
      projectedProperties: createEventProjectedProps(assetSignals)
    }
  };
};

// NOTE: Requires all assetSignals to be from the same asset
const createTsiEventRequest = (assetSignals, timespan, tsiUrl, tsiToken) => {
  const requestData = createEventRequestData(assetSignals, timespan);

  return {
    method: 'POST',
    url: tsiUrl,
    headers: {
      Authorization: `Bearer ${tsiToken}`
    },
    data: requestData
  };
};

const createAggregateInlineVariables = (assetSignals) => {
  return assetSignals.signals.reduce((inlineVariablesObj, signal) => {
    for (let aggregate of signal.aggregates) {
      inlineVariablesObj[toAggregateName(signal.name, aggregate)] = {
        kind: 'numeric',
        value: {
          tsx: `$event.${signal.name}.${mapToTsiSignalType(signal.type)}`
        },
        aggregation: {
          tsx: `${aggregate}($value)`
        }
      };
    }

    return inlineVariablesObj;
  }, {});
};

const createAggregateProjectedProps = (assetSignals) => {
  let projectedProps = [];

  for (let signal of assetSignals.signals) {
    projectedProps = projectedProps.concat(signal.aggregates.map(aggregate => toAggregateName(signal.name, aggregate)));
  }

  return projectedProps;
};

export const createAggregateRequestData = (assetSignals, timespan, aggregate) => {
  const intervalString = _.isString(aggregate) ? aggregate : aggregate.toISO();

  return {
    aggregateSeries: {
      timeSeriesId: [assetSignals.assetId],
      searchSpan: timespan,
      interval: intervalString,
      inlineVariables: createAggregateInlineVariables(assetSignals),
      projectedVariables: createAggregateProjectedProps(assetSignals)
    }
  };
};

// NOTE: Requires all assetSignals to be from the same asset
const createAggregateRequest = (assetSignals, timespan, aggregate, tsiUrl, tsiToken) => {

  const requestData = createAggregateRequestData(assetSignals, timespan, aggregate);

  return {
    method: 'POST',
    url: tsiUrl,
    headers: {
      Authorization: `Bearer ${tsiToken}`
    },
    data: requestData
  };
};

const getEventData = async (assetSignalsSet, timespan, tsiToken) => {
  const tsiUrl = createTsiUrl(tsiId);
  let allData = [];

  for (let assetSignals of assetSignalsSet) {
    const requestConfig = createTsiEventRequest(
      assetSignals,
      timespan,
      tsiUrl,
      tsiToken);

    const requestResult = await axios(requestConfig);
    allData = allData.concat(convertTsiDataToGraphData(requestResult.data, assetSignals));
  }

  return allData;
};

const getAggregateData = async (assetSignalsSet, timespan, aggregate, tsiToken) => {
  const tsiUrl = createTsiUrl(tsiId);
  let allData = [];

  for (let assetSignals of assetSignalsSet) {
    const requestConfig = createAggregateRequest(
      assetSignals,
      timespan,
      aggregate,
      tsiUrl,
      tsiToken
    );

    const requestResult = await axios(requestConfig);
    allData = allData.concat(convertTsiDataToGraphData(requestResult.data, assetSignals));
  }

  return allData;
};

const loadTelemetry = async (assetSignalsSet, timespan, aggregate, tsiToken) => {
  if (aggregate === TimeBucket.RAW) {
    return getEventData(assetSignalsSet, toTSITimespan(timespan), tsiToken);
  }
  return getAggregateData(assetSignalsSet, toTSITimespan(timespan), aggregate, tsiToken);

};

export { loadTelemetry };
